/**
 * In JavaScript, primitives are passed by value while objects are passed by
 * reference. This function can be used in situations where you want to pass an
 * object by value.
 *
 * To know more, please visit:
 * https://developer.mozilla.org/en-US/docs/Glossary/Deep_copy
 * @param object
 */
export function deepCopy<T>(object: T) {
  return JSON.parse(JSON.stringify(object));
}

/**
 * Robust deep equality check for arrays and objects using recursive comparison
 * Based on best practices from fast-deep-equal and lodash.isEqual implementations
 * @param a First value to compare
 * @param b Second value to compare
 * @returns true if values are deeply equal, false otherwise
 */
export function deepEqual<T>(a: T, b: T): boolean {
  // Same reference or primitive equality
  if (a === b) {
    return true;
  }

  // Handle null/undefined cases with strict equality
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  // Type check - must be same type
  if (typeof a !== typeof b) {
    return false;
  }

  // For primitive types, use strict equality
  if (typeof a !== 'object') {
    return a === b;
  }

  // Handle Date objects
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // Handle RegExp objects
  if (a instanceof RegExp && b instanceof RegExp) {
    return a.toString() === b.toString();
  }

  // Handle arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }

  // Handle case where one is array and other is not
  if (Array.isArray(a) || Array.isArray(b)) {
    return false;
  }

  // Handle objects
  const keysA = Object.keys(a as Record<string, any>);
  const keysB = Object.keys(b as Record<string, any>);

  // Different number of keys
  if (keysA.length !== keysB.length) {
    return false;
  }

  // Check each key-value pair
  for (const key of keysA) {
    // Key doesn't exist in second object
    if (!Object.prototype.hasOwnProperty.call(b, key)) {
      return false;
    }

    // Recursively compare values
    if (!deepEqual((a as any)[key], (b as any)[key])) {
      return false;
    }
  }

  return true;
}
